import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import type { ApiSliceType, PublicApiSliceType } from "../../../types/interfaces";
import type { EmissionsByGas } from "@typedefs/resultTypes";
import type { ChangesResponseType } from "../../../components/actions/changes/changesTypes";
import { Country, ModuleType } from "../../../types/interfaces";
import type { EndpointBuilder } from "@reduxjs/toolkit/query";
import { ModuleTypeRegistry } from "@utils/moduleTypeRegistry";

// Types
interface ModuleResPayload {
	uniqueId: number;
	path: string;
	cacheTags?: TagTypes[];
}

export interface GetDefaultProps { 
    uniqueId: number, 
    route: string, 
    cacheTag: TagTypes 
}

export interface GetModuleTypesPayload {
	isContainer?: boolean;
	isSubmodule?: boolean;
}

/**
 * Creates API endpoints for generic operations like fetching module results, history, and defaults
 * Used by both private and public API slices
 */
export function createGenericOperationsApi(apiSlice: ApiSliceType | PublicApiSliceType) {
    const endpoints = (builder: EndpointBuilder<any, TagTypes, string>) => ({
        getModuleResultsByGas: builder.query<EmissionsByGas, ModuleResPayload>({
            query: ({ path, uniqueId }) => `${path}/${uniqueId}/results/?aggregate=gas`,
            providesTags: (_res, _error, { uniqueId, path }) => [
                { type: "ModuleResults", id: path !== "land-use-changes" ? `${path}-${uniqueId}` : "land-use-changes" },
            ],
        }),
        getModuleById: builder.query<unknown, ModuleResPayload>({
            query: ({ path, uniqueId }) => `${path}/${uniqueId}/`,
            providesTags: (_res, _error, { uniqueId, cacheTags }) => cacheTags?.map((tag) => ({ type: tag, id: uniqueId })) ?? []
        }),
        getModuleHistory: builder.query<ChangesResponseType[], ModuleResPayload>({
            query: ({ path, uniqueId }) => `${path}/${uniqueId}/history/`,
            transformResponse: (response: ChangesResponseType[]) => response.map((change) => ({ ...change, date: new Date(change.date).toISOString() })),
            providesTags: (_res, _error, { uniqueId, path }) => [
                { type: "ModuleHistory", id: `${path}-${uniqueId}` },
            ],
        }),
        getSubmoduleDefaults: builder.query<any, GetDefaultProps>({
            query: ({ uniqueId, route }) => `${route}/${uniqueId}/defaults/`,
            providesTags: (_res, _error, { uniqueId, cacheTag }) => [{ type: cacheTag, id: uniqueId }],
        }),
        getModuleTypes: builder.query<ModuleType[], GetModuleTypesPayload>({
            query: ({ isContainer, isSubmodule }) => {
                const queryParams = new URLSearchParams();
                if (isContainer !== undefined) queryParams.append("is_container", isContainer.toString());
                if (isSubmodule !== undefined) queryParams.append("is_submodule", isSubmodule.toString());

                return `module-types/?${queryParams.toString()}`;
            },
            transformResponse: (response: { results?: ModuleType[] } | ModuleType[], _meta, { isContainer, isSubmodule }) => {
                const moduleTypes = Array.isArray(response) ? response : (response.results ?? []);
                // The unfiltered list is the source of the ModuleTypeRegistry. Filling it
                // here (rather than in a component effect) guarantees the registry is ready
                // by the time any component can observe this query's data, so callers can
                // gate registry lookups on `data` being present.
                if (isContainer === undefined && isSubmodule === undefined)
                    ModuleTypeRegistry.getInstance().initialize(moduleTypes);
                return moduleTypes;
            },
        }),
        getModuleTypeById: builder.query<ModuleType, number>({
            query: (id) => `module-types/${id}/`,
            providesTags: (_res, _error, id) => [{ type: "ModuleTypes", id }],
        }),
        getDefaultCountry: builder.query<Country, number>({
            query: (countryId) => `countries/${countryId}/`,
        }),
    });

    return apiSlice.injectEndpoints({ endpoints });
}

// Create both private and public API slices
export const genericOperationsApiSlice = createGenericOperationsApi(apiSlice);
export const publicGenericOperationsApiSlice = createGenericOperationsApi(publicApiSlice);

export const genericOperationsEndpoints = genericOperationsApiSlice.endpoints;

// Export hooks for private API
export const {
    useGetModuleResultsByGasQuery,
    useGetModuleHistoryQuery,
    useGetSubmoduleDefaultsQuery,
    useGetModuleTypesQuery,
    useGetModuleTypeByIdQuery,
    useGetDefaultCountryQuery,
    useGetModuleByIdQuery,
} = genericOperationsApiSlice;

// Export hooks for public API
export const {
    useGetModuleResultsByGasQuery: usePublicGetModuleResultsByGasQuery,
    useGetModuleHistoryQuery: usePublicGetModuleHistoryQuery,
    useGetSubmoduleDefaultsQuery: usePublicGetSubmoduleDefaultsQuery,
    useGetModuleTypesQuery: usePublicGetModuleTypesQuery,
    useGetModuleTypeByIdQuery: usePublicGetModuleTypeByIdQuery,
    useGetDefaultCountryQuery: usePublicGetDefaultCountryQuery,
    useGetModuleByIdQuery: usePublicGetModuleByIdQuery,
} = publicGenericOperationsApiSlice; 